import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Inject,
  OnChanges
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { IFilterActionButtonLabels, IFilter } from 'src/libs/vi-ui/src/lib/components/filters/generic-filter/interfaces';

import { BehaviorSubject, Subscription } from 'rxjs';

import { isEqual } from 'lodash-es';

import { getKeyCode, KeyCode } from '../../../utils';

interface IAppliedFilter {
  id: string;
  [key: string]: unknown;
}

const FILTER_ACTIONS = {
  APPLY: 'apply',
  CLEAR: 'clear',
  CLEAR_ALL: 'clearAll'
} as const;

enum EmissionFilterMode {
  ALL = 'all',
  CURRENT_ONLY = 'current_only'
}

@Component({
  selector: 'vi-generic-filter',
  templateUrl: './generic-filter.component.html',
  styleUrls: ['./generic-filter.component.scss'],
  standalone: false
})
export class GenericFilterComponent implements OnInit, OnDestroy, OnChanges {
  @Input() filterTitle: string = 'Filter';
  @Input() filterContentHeaderTitle: string;
  @Input() isOpen: boolean = false;
  @Input() shouldForceSingleSelection: boolean = false;

  // The values that are actually applied and visible in the filter badge
  @Input() appliedFilters: IAppliedFilter[] = [];

  /**
   * Controls how filter values are emitted:
   * - 'all': Emit all selected values as an array (default behavior)
   * - 'last': Emit only the last/most recent selected value
   */
  @Input() emissionFilterValuesMode: EmissionFilterMode;

  @Input() inlineFilter: boolean = false;
  @Input() showApplyButton?: boolean;
  @Input() showClearButton?: boolean;
  @Input() showClearAllButton?: boolean;
  @Input() filterActionButtonLabels: IFilterActionButtonLabels;

  // Search functionality
  @Input() searchable: boolean = false;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() searchQuery: string = '';
  @Input() searchDebounceTime: number = 300;

  // Items and loading state
  @Input() items: IFilter[] = [];
  @Input() isLoading: boolean = false;
  @Input() hasMore: boolean = false;
  @Input() shouldShowDraftFiltersCount: boolean = true;
  @Input() shouldShowAppliedFiltersCount: boolean = true;

  // ScrollListContainer inputs
  @Input() itemSize: number = 20;
  @Input() isVirtualScrollActive: boolean = false;
  @Input() minBufferPx: number;
  @Input() maxBufferPx: number;

  @Output() filterToggled = new EventEmitter<boolean>();
  @Output() filterApplied = new EventEmitter<unknown[]>();
  @Output() filterCleared = new EventEmitter<void>();
  // Note: when filter is opened/closed, this event is emitted and we can decide what we do with data and state
  @Output() filterOpened = new EventEmitter<boolean>();
  @Output() filterClosed = new EventEmitter<void>();
  @Output() allFiltersCleared = new EventEmitter<void>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() loadMore = new EventEmitter<void>();

  @ViewChild('filterContainer') filterContainer: ElementRef;
  @ViewChild('filterButton') filterButton: ElementRef;

  // Content projections for customizable parts
  @ContentChild('customFilterActionButton') customFilterActionButton: TemplateRef<unknown>;
  @ContentChild('customTitle') customTitle: TemplateRef<unknown>;
  @ContentChild('filterContent') filterContent: TemplateRef<unknown>;
  @ContentChild('customBottomSection') customBottomSection: TemplateRef<unknown>;
  @ContentChild('customApplyButton') customApplyButton: TemplateRef<unknown>;
  @ContentChild('customClearButton') customClearButton: TemplateRef<unknown>;
  @ContentChild('customClearAllButton') customClearAllButton: TemplateRef<unknown>;
  @ContentChild('customSearchInput') customSearchInput: TemplateRef<unknown>;

  // Templates mapping between generic filter and ScrollListContainer templates
  @ContentChild('itemTemplate') itemTemplate: TemplateRef<unknown>;
  @ContentChild('emptyState') emptyState: TemplateRef<unknown>;
  @ContentChild('loadingMoreState') loadingMoreState: TemplateRef<unknown>;
  @ContentChild('loadingState') loadingState: TemplateRef<unknown>;

  // The current draft values that are being worked on in the filter panel
  // These are only applied to appliedFilters when the Apply button is clicked
  public draftSelectedFilters: IAppliedFilter[] = [];
  public draftSearchQuery: string = '';

  // Keyboard navigation state
  public focusedItemIndex: number = -1;
  public focusedActionIndex: number = -1;
  public isKeyboardNavigation: boolean = false;

  public get visibleItems() {
    return this.items || [];
  }

  public get focusedItemClass() {
    return 'filter-item-focused';
  }

  // Search subject
  private searchSubject = new BehaviorSubject<string>('');
  private searchSubscription: Subscription;

  // Unified event listeners
  private eventListeners: { type: string; handler: EventListener }[] = [];

  constructor(@Inject(DOCUMENT) private document: Document, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    // Setting the emission mode to either the default prop provided to this component, or compute it based on the inlineFilter
    this.emissionFilterValuesMode = this.emissionFilterValuesMode
      ? this.emissionFilterValuesMode
      : this.inlineFilter
      ? EmissionFilterMode.CURRENT_ONLY
      : EmissionFilterMode.ALL;

    if (this.searchable) {
      this.setupSearchObservable();
    }
  }

  public ngOnChanges(changes: { [propertyName: string]: { currentValue: unknown; previousValue: unknown; firstChange: boolean } }): void {
    // Don't auto-focus any items when items are loaded
    // Let the user initiate keyboard navigation with arrow keys
  }

  public ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.unbindOutsideContainerClickEvents();
  }

  public toggleFilter = (): void => {
    this.isOpen = !this.isOpen;
    this.filterToggled.emit(this.isOpen);

    if (this.isOpen) {
      this.openFilter();
    } else {
      this.closeFilter();
    }
  };

  public onApplyFilter = (): void => {
    this.appliedFilters = [...this.draftSelectedFilters];
    this.searchQuery = this.draftSearchQuery;
    const formattedFilters = this.formatFilterForApply(this.appliedFilters);

    // Emit based on emission mode
    if (this.emissionFilterValuesMode === EmissionFilterMode.CURRENT_ONLY && formattedFilters.length > 0) {
      // Emit only the last selected value (not as an array)
      this.filterApplied.emit([formattedFilters[formattedFilters.length - 1]]);
    } else {
      // Default behavior: emit all values as an array
      this.filterApplied.emit(formattedFilters);
    }

    // Always close the filter panel after applying
    if (this.isOpen) {
      this.closeFilter();
    }
  };

  public onClear = (): void => {
    this.draftSelectedFilters = this.appliedFilters;
    this.cdr.markForCheck();
  };

  public onClearAll = (options?: { shouldClearAppliedFilters: boolean }): void => {
    this.draftSelectedFilters = [];
    this.draftSearchQuery = '';
    this.searchQuery = '';
    this.allFiltersCleared.emit();
    if (options && options.shouldClearAppliedFilters) {
      this.appliedFilters = [];
    }
    this.cdr.markForCheck();
  };

  onSearchInput = (event: Event): void => {
    const value = (event.target as HTMLInputElement).value;
    this.draftSearchQuery = value;
    this.searchSubject.next(value);
  };

  public onLoadMoreItems = (): void => {
    this.loadMore.emit();
  };

  public pressItem = (item: IFilter): void => {
    const filterObject = this.createFilterObject(item);
    if (!filterObject) {
      return;
    }

    // Update focus to the selected item
    const itemPosition = this.visibleItems.findIndex(visibleItem => visibleItem.id === item.id);
    if (itemPosition >= 0) {
      this.focusedItemIndex = itemPosition;
    }

    const itemIndex = this.draftSelectedFilters.findIndex(value => isEqual(value, filterObject));

    if (this.shouldForceSingleSelection) {
      this.draftSelectedFilters = itemIndex > -1 ? [] : [filterObject];
      this.cdr.markForCheck();

      // If inline filter, apply and close when selecting
      if (this.inlineFilter) {
        this.onApplyFilter();
      }
      return;
    }

    if (itemIndex > -1) {
      this.draftSelectedFilters = this.draftSelectedFilters.filter((_, index) => index !== itemIndex);
    } else {
      this.draftSelectedFilters = [...this.draftSelectedFilters, filterObject];
    }

    this.cdr.markForCheck();

    // If inline filter, apply and close when selecting
    if (this.inlineFilter) {
      this.onApplyFilter();
    }
  };

  public navigateUp(): void {
    this.isKeyboardNavigation = true;
    if (this.focusedItemIndex > 0) {
      this.focusedItemIndex--;
    } else if (this.focusedItemIndex === 0) {
      // Wrap to last item when at first item
      this.focusedItemIndex = this.visibleItems.length - 1;
    }
    this.cdr.markForCheck();
    this.scrollToFocusedItem();
  }

  public navigateDown(): void {
    this.isKeyboardNavigation = true;
    // If no item is focused yet, start at the first item
    if (this.focusedItemIndex === -1) {
      this.focusedItemIndex = 0;
    } else if (this.focusedItemIndex < this.visibleItems.length - 1) {
      this.focusedItemIndex++;
    } else {
      // Wrap to first item when at last item
      this.focusedItemIndex = 0;
    }
    this.cdr.markForCheck();
    this.scrollToFocusedItem();
  }

  public selectFocusedItem(): void {
    if (this.focusedItemIndex >= 0 && this.focusedItemIndex < this.visibleItems.length) {
      const item = this.visibleItems[this.focusedItemIndex];
      if (this.inlineFilter && this.isItemSelected(item)) {
        // When using inline filters, we cant select an already selected item, we must delete it from the selection
        return;
      }
      this.pressItem(item);
    }
  }

  public isItemFocused(index: number): boolean {
    return this.focusedItemIndex === index && this.isKeyboardNavigation;
  }

  public isItemSelected = (item: IFilter): boolean => {
    const filterObject = this.createFilterObject(item);
    if (!filterObject) {
      return false;
    }

    // We always compare against the draft since applied filters are stable state that changes only on Apply
    // Draft is what user is currently working on
    const isDraftSelected = this.draftSelectedFilters.some(filter => isEqual(filter.id, filterObject.id));
    return isDraftSelected;
  };

  public getItemContext = (id: string): (IFilter & { isSelected: boolean; isFocused: boolean; pressItem: () => void }) | null => {
    const item = this.items.find(i => i.id === id);
    if (!item) {
      return null;
    }

    const itemContext = {
      ...item,
      label: item.label,
      id: item.id,
      value: item.value,
      isSelected: this.isItemSelected(item),
      isFocused: this.isItemFocused(this.items.findIndex(i => i.id === id)),
      pressItem: () => this.pressItem(item)
    };

    this.cdr.markForCheck();
    return itemContext;
  };

  public openFilter(omitFocus = false): void {
    this.isOpen = true;
    this.bindOutsideContainerClickEvents();
    this.filterOpened.emit();
    this.draftSelectedFilters = [...this.appliedFilters];
    this.draftSearchQuery = this.searchQuery;
    // Don't auto-focus any item - let user start keyboard navigation when they want
    this.focusedItemIndex = -1;
    this.focusedActionIndex = -1;
    this.isKeyboardNavigation = false;
    this.cdr.markForCheck();

    if (!omitFocus) {
      setTimeout(() => {
        if (this.filterContainer && this.filterContainer.nativeElement) {
          this.filterContainer.nativeElement.focus();
        }
      }, 0);
    }
  }

  public closeFilter(): void {
    this.isOpen = false;
    this.filterClosed.emit();
    this.unbindOutsideContainerClickEvents();
    this.focusedItemIndex = -1;
    this.focusedActionIndex = -1;
    this.cdr.detectChanges();
  }

  public handleFilterContainerKeyUp(event: KeyboardEvent): void {
    let code;
    // We use try catch since our function throws where there is no key code found to compare against the function
    try {
      code = getKeyCode(event);
    } catch {
      code = '';
    }
    switch (code) {
      case KeyCode.Escape:
        this.closeFilter();
        return;
      case KeyCode.ArrowUp:
        event.preventDefault();
        this.navigateUp();
        return;
      case KeyCode.ArrowDown:
        event.preventDefault();
        this.navigateDown();
        return;
      case KeyCode.Tab:
        event.preventDefault();
        this.navigateToActions();
        return;
      case KeyCode.Enter:
      case KeyCode.Space:
        event.preventDefault();
        if (this.focusedActionIndex >= 0) {
          this.activateAction();
        } else {
          this.selectFocusedItem();
        }
        return;
    }
  }

  public navigateToActions(): void {
    if (this.focusedActionIndex < 0) {
      // Move from content to first action
      this.focusedActionIndex = 0;
      this.focusedItemIndex = -1;
    } else {
      // Move to next action or wrap to filter button
      const maxActions = this.availableActionFunctions.length;

      if (this.focusedActionIndex < maxActions - 1) {
        this.focusedActionIndex++;
      } else {
        // Tab out to filter button
        if (this.filterButton && this.filterButton.nativeElement) {
          this.filterButton.nativeElement.focus();
          this.focusedActionIndex = -1;
          this.focusedItemIndex = this.visibleItems.length > 0 ? 0 : -1;
        }
      }
    }
    this.cdr.markForCheck();
  }

  public activateAction(): void {
    const actionFunctions = this.availableActionFunctions;
    const currentAction = actionFunctions[this.focusedActionIndex];

    if (currentAction && !currentAction.isDisabled) {
      currentAction.execute();
    }
  }

  public isActionFocused(action: string): boolean {
    const actions = [];
    if (this.shouldShowApplyButton) {
      actions.push(FILTER_ACTIONS.APPLY);
    }
    if (this.shouldShowClearButton) {
      actions.push(FILTER_ACTIONS.CLEAR);
    }
    if (this.shouldShowClearAllButton) {
      actions.push(FILTER_ACTIONS.CLEAR_ALL);
    }

    const actionIndex = actions.indexOf(action);
    return this.focusedActionIndex === actionIndex;
  }

  private scrollToFocusedItem(): void {
    // This will ensure the focused item is visible in the scroll container
    // Implementation depends on the scroll container component
    const focusedElement = this.document.querySelector(`.${this.focusedItemClass}`);
    if (focusedElement) {
      focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  public get isApplyDisabled() {
    return !this.draftSelectedFilters || isEqual(this.draftSelectedFilters, this.appliedFilters);
  }

  public get isClearDisabled() {
    return isEqual(this.draftSelectedFilters, this.appliedFilters);
  }

  public get isClearAllDisabled() {
    return this.draftSelectedFilters.length === 0 && this.draftSearchQuery.length === 0;
  }

  public get shouldShowApplyButton(): boolean {
    return this.showApplyButton !== undefined ? this.showApplyButton : !this.inlineFilter;
  }

  public get shouldShowClearButton(): boolean {
    return this.showClearButton !== undefined ? this.showClearButton : !this.inlineFilter;
  }

  public get shouldShowClearAllButton(): boolean {
    return this.showClearAllButton !== undefined ? this.showClearAllButton : !this.inlineFilter;
  }

  public get filterActionButtonDefinitions() {
    return {
      isOpen: this.isOpen,
      toggleFilter: this.toggleFilter,
      openFilter: this.openFilter,
      closeFilter: this.closeFilter,
      draftSelectedFilters: this.draftSelectedFilters?.length,
      hasFilters: this.appliedFilters?.length > 0,
      isVisible: this.shouldShowAppliedFiltersCount
    };
  }

  public get headerDefinitions() {
    return {
      title: this.filterContentHeaderTitle || this.filterTitle
    };
  }

  public get bottomSectionDefinitions() {
    return {
      applyButtonLabel: this.filterActionButtonLabels.Apply,
      applyFilter: this.onApplyFilter,
      isApplyDisabled: this.isApplyDisabled,
      clearButtonLabel: this.filterActionButtonLabels.Cancel,
      clearFilter: this.onClear,
      isClearDisabled: this.isClearDisabled,
      clearAllButtonLabel: this.filterActionButtonLabels.ClearAllButton,
      clearAllFilters: this.onClearAll,
      isClearAllDisabled: this.isClearAllDisabled,
      draftSelectedFilters: this.draftSelectedFilters?.length,
      appliedCount: this.appliedFilters?.length,
      hasFilters: this.draftSelectedFilters?.length > 0,
      isVisible: this.shouldShowDraftFiltersCount
    };
  }

  private get availableActionFunctions() {
    const actions = [];
    if (this.shouldShowApplyButton) {
      actions.push({ execute: this.onApplyFilter, isDisabled: this.isApplyDisabled });
    }

    if (this.shouldShowClearButton) {
      actions.push({ execute: this.onClear, isDisabled: this.isClearDisabled });
    }

    if (this.shouldShowClearAllButton) {
      actions.push({ execute: this.onClearAll, isDisabled: this.isClearAllDisabled });
    }
    return actions;
  }

  /**
   * Creates a standardized filter object from an IFilter item
   * @param item The filter item to convert to a standardized format
   * @returns An object with the filter's field and value
   */
  private createFilterObject(item: IFilter): IAppliedFilter {
    if (!item || !item.value || !item.updatedField) {
      return null;
    }
    return { id: item.id, [item.updatedField]: item.value };
  }

  private setupSearchObservable(): void {
    this.searchSubscription = this.searchSubject.pipe().subscribe(value => {
      this.searchChanged.emit(value);
    });
  }

  private addEventHandler(type: string, handler: EventListener): void {
    const boundHandler = handler.bind(this);
    this.document.addEventListener(type, boundHandler);
    this.eventListeners.push({ type, handler: boundHandler });
  }

  private bindOutsideContainerClickEvents(): void {
    this.addEventHandler('mouseup', this.handleOutsideContainerClick);
  }

  private unbindOutsideContainerClickEvents(): void {
    this.eventListeners.forEach(listener => {
      this.document.removeEventListener(listener.type, listener.handler);
    });
    this.eventListeners = [];
  }

  private handleOutsideContainerClick(event: MouseEvent): void {
    if (this.filterContainer && this.filterContainer.nativeElement) {
      const isClickInside = this.filterContainer.nativeElement.contains(event.target as Node);
      const isClickOnButton = this.filterButton && this.filterButton.nativeElement && this.filterButton.nativeElement.contains(event.target as Node);
      if (!isClickInside && !isClickOnButton) {
        this.closeFilter();
      }
    }
  }

  private formatFilterForApply(filters: IAppliedFilter[]): Record<string, unknown>[] {
    return filters.map(filter => {
      const filterKey = Object.keys(filter).find(key => key !== 'id');
      if (!filterKey) {
        // When filter has only id field that we don't want to omit
        // We return the filter as is
        return { id: filter.id };
      }
      return { [filterKey]: filter[filterKey] };
    });
  }
}
